import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Recorrido guiado para las cuentas demo (@cfdemo.com). Pensado para que el
// closer o un cliente vean la app paso a paso: pizarra → partido → estadísticas.
const STEPS = {
  coach: [
    { path: "/coach", title: "Bienvenido al Modo Demo · Entrenador", body: "Este recorrido te enseña lo que ve un entrenador: su panel, la pizarra táctica, cómo registrar un partido y las estadísticas automáticas. Pulsa «Siguiente» para empezar." },
    { path: "/coach", sel: '[data-testid="go-jornadas-btn"]', title: "Mis Equipos", body: "El entrenador solo ve SUS equipos asignados — privacidad total por rol. Desde aquí accede directo a registrar jornadas." },
    { path: "/coach/pizarra", sel: '[data-testid="pz-team"]', title: "Pizarra táctica · La joya de la demo", body: "Elige equipo, rival y fecha. La pizarra permite colocar la alineación sobre el campo y seguir el partido en vivo: goles, cambios y tarjetas con un toque." },
    { path: "/coach/pizarra", sel: '[data-testid="pz-start"]', title: "Partido en vivo", body: "Al pulsar «Iniciar», se abre el campo interactivo con cronómetro. Cada evento que marcas aquí se guarda luego como jornada oficial — sin duplicar trabajo." },
    { path: "/coach/jornadas", sel: '[data-testid="new-jornada-btn"]', title: "Jornadas · Registro en 3 minutos", body: "La otra vía: registrar el partido a posteriori. Convocatoria automática, minutos, goles, tarjetas y observaciones. Tres pantallas, sin líos." },
    { path: "/coach/jornadas/new", title: "Registrar un partido", body: "Selecciona el equipo y pulsa «Auto-convocatoria» para llamar a toda la plantilla de golpe. Después solo marcas minutos jugados, goleadores y tarjetas." },
    { path: "/stats", sel: '[data-testid="stats-table"]', title: "Estadísticas automáticas", body: "Cada jornada registrada alimenta estas estadísticas sola: minutos, goles, tarjetas y asistencia por jugador. Se pueden filtrar y exportar a CSV." },
    { path: "/stats", title: "Fin del recorrido 🎉", body: "Esto es lo esencial del día a día de un entrenador. Recuerda: en el menú tienes la «📖 Guía de uso» con capturas reales de cada pantalla por si el cliente pregunta algo más." },
  ],
  coordinator: [
    { path: "/coordinator", title: "Bienvenido al Modo Demo · Coordinador", body: "Este recorrido muestra la visión global del club que tiene un coordinador: todos los equipos, el calendario y las estadísticas completas. Pulsa «Siguiente»." },
    { path: "/coordinator", title: "Vista Global del club", body: "El coordinador ve TODOS los equipos del club de un vistazo: jornadas registradas, actividad de los entrenadores y alertas. Es su centro de control." },
    { path: "/coordinator/teams", title: "Todos los equipos", body: "Acceso a cada equipo del club con su plantilla, cuerpo técnico y jornadas. El coordinador supervisa sin tener que pedir informes a nadie." },
    { path: "/calendar", title: "Calendario del club", body: "Todas las jornadas de todos los equipos en un único calendario. Verde = registrada, pendientes a la vista. Ideal para detectar equipos rezagados." },
    { path: "/stats", sel: '[data-testid="stats-table"]', title: "Estadísticas de todo el club", body: "Minutos, goles y asistencia de TODOS los jugadores del club, filtrables por categoría y equipo. Exportables a CSV con un clic." },
    { path: "/stats", title: "Fin del recorrido 🎉", body: "Con esto el coordinador controla el club entero sin perseguir a nadie. En el menú tienes la «📖 Guía de uso» con capturas reales de cada pantalla." },
  ],
  club_admin: [
    { path: "/club", title: "Bienvenido al Modo Demo · Admin del Club", body: "Este recorrido muestra lo que ve el presidente o directivo: el panel del club, equipos, estadísticas y la facturación. Pulsa «Siguiente»." },
    { path: "/club", title: "Panel del club", body: "Resumen total: categorías, equipos, jugadores y actividad reciente. También sube aquí el escudo del club para personalizar la app y los informes PDF." },
    { path: "/club/teams", title: "Equipos y categorías", body: "El admin estructura el club: crea categorías (Benjamín, Alevín…), equipos y asigna entrenadores. Todo el club queda organizado en minutos." },
    { path: "/club/staff", title: "Equipo técnico", body: "Desde aquí invita a coordinadores y entrenadores por email. Cada uno entra con su propio acceso y solo ve lo que le corresponde." },
    { path: "/stats", sel: '[data-testid="stats-table"]', title: "Estadísticas del club", body: "Visión completa del rendimiento: minutos, goles, tarjetas y asistencia de todos los jugadores. Filtros por categoría, equipo y temporada." },
    { path: "/billing", title: "Facturación", body: "El único rol que ve esta pantalla. Aquí el club elige plan, paga con tarjeta (Stripe) y gestiona su suscripción. Sin llamadas ni transferencias." },
    { path: "/billing", title: "Fin del recorrido 🎉", body: "Control total del club en una sola app. Recuerda la «📖 Guía de uso» del menú, con capturas reales pantalla a pantalla para resolver cualquier duda." },
  ],
};
STEPS.director_deportivo = STEPS.coordinator;
STEPS.delegado = STEPS.coach;

export const isDemoAccount = (user) => (user?.email || "").endsWith("@cfdemo.com");

export function DemoTour({ role, onClose }) {
  const steps = STEPS[role] || [];
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const pollRef = useRef(null);
  const step = steps[idx];

  useEffect(() => {
    if (!step) return;
    if (location.pathname !== step.path) navigate(step.path);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Oculta el widget de chat GHL mientras dura el tour (roba clics y tapa la tarjeta).
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = "chat-widget{display:none!important}";
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  // Localiza el elemento destacado (poll: la página puede estar cargando datos).
  useEffect(() => {
    if (!step) return;
    setRect(null);
    clearInterval(pollRef.current);
    if (!step.sel) return;
    let tries = 0;
    pollRef.current = setInterval(() => {
      tries++;
      const el = document.querySelector(step.sel);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries > 25) {
        clearInterval(pollRef.current);
      }
    }, 250);
    return () => clearInterval(pollRef.current);
  }, [idx, step, location.pathname]);

  if (!step) return null;
  const last = idx === steps.length - 1;

  // Posición de la tarjeta: bajo el elemento destacado o centrada.
  const cardStyle = rect
    ? {
        position: "fixed",
        top: Math.min(rect.top + rect.height + 14, window.innerHeight - 290),
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 392)),
      }
    : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

  return (
    <div className="fixed inset-0" style={{ zIndex: 2147483000 }} data-testid="demo-tour-overlay">
      {rect ? (
        <div
          className="fixed border-2 border-primary rounded-sm transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-black/72" style={{ background: "rgba(0,0,0,0.72)" }} />
      )}

      <div style={cardStyle} className="w-[min(380px,calc(100vw-24px))] bg-card border-2 border-primary shadow-2xl p-5" data-testid="demo-tour-card">
        <div className="flex items-start justify-between gap-3">
          <div className="overline text-[10px] text-primary">🎬 Modo Demo · Paso {idx + 1} de {steps.length}</div>
          <button data-testid="demo-tour-close" onClick={onClose} className="text-muted-foreground hover:text-foreground -mt-1" aria-label="Salir del modo demo">
            <X size={16} />
          </button>
        </div>
        <div className="font-heading font-black text-lg mt-1 leading-tight">{step.title}</div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <button
            data-testid="demo-tour-prev"
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="h-9 px-3 border border-border text-xs uppercase font-heading tracking-wider inline-flex items-center gap-1 disabled:opacity-30 hover:border-primary"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-primary" : "bg-border"}`} />
            ))}
          </div>
          <button
            data-testid="demo-tour-next"
            onClick={() => (last ? onClose() : setIdx(i => i + 1))}
            className="h-9 px-3 bg-primary text-primary-foreground text-xs uppercase font-heading tracking-wider inline-flex items-center gap-1 hover:opacity-90"
          >
            {last ? "Terminar" : "Siguiente"} {!last && <ChevronRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
